import { call, put } from 'redux-saga/effects';

import { CERTIFIED_INDICATOR_TYPE } from '~src/app/quality/DataQualityType';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { DataSources } from '~src/datasource/resources/Datasources';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Databases } from '../../../../catalog/resources/Databases';
import { Tables } from '../../../../catalog/resources/Tables';
import { Messages } from '../../../../codegen/Localize';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { assertUnreachable } from '../../../../ts/helpers/TypescriptHelpers';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { SetCertificationAction } from '../../../actions/spaces/SetCertificationAction';
import { CertificationDialogState, getCertificationActionDialogOptions } from '../../../content/CertificationDialog';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';

export function* handleSetCertification(action: SetCertificationAction) {
  try {
    yield call(openCertificationDialog, action);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function setCertification(item: SetCertificationAction['item'], args: CertificationDialogState): Promise<IResult> {
  switch(item.contentType) {
    case 'database': return Databases.setDatabaseCertification({
      certificationNote: args.certificationNote,
      isCertified: args.isCertified,
      id: item.vizportalId,
      luid: item.luid,
    });
    case 'table': return Tables.setTableCertification({
      certificationNote: args.certificationNote,
      isCertified: args.isCertified,
      id: item.vizportalId,
      luid: item.luid,
    });
    case 'datasource': return DataSources.setCertification({
      certificationNote: args.certificationNote,
      isCertified: args.isCertified,
      id: item.vizportalId,
      luid: item.luid,
    });
    case 'publishedConnection': return PublishedConnections.setCertification({
      certificationNote: args.certificationNote,
      isCertified: args.isCertified,
      id: item.vizportalId,
      luid: item.luid,
    });
    case 'publishedConnectionTable': return PublishedConnectionTables.setCertification({
      certificationNote: args.certificationNote,
      isCertified: args.isCertified,
      id: item.vizportalId,
      luid: item.luid,
    });
    default: assertUnreachable(item);
  }
}

function getCertificationOrDefault(item: SetCertificationAction['item']): CertificationDialogState {
  const certification = item.dataQualityIndicators?.filter(i => i.type === CERTIFIED_INDICATOR_TYPE)?.shift() || null;

  if (certification) {
    return {
      isCertified: certification.isActive,
      certificationNote: certification.message,
    };
  }

  return {
    isCertified: item.isCertified || false,
    certificationNote: item.certificationNote || ''
  };
}

export function openCertificationDialog(action: SetCertificationAction) {
  const modalInstance = openReactActionDialog(getCertificationActionDialogOptions({
    initialBodyProps: { useMarkup: FeatureFlags.markupEditingEnabled },
    initialFormData: getCertificationOrDefault(action.item),
    doAction: (formData) => setCertification(action.item, formData)
  }));

  return modalInstance.result;
}
